import React, { PropsWithChildren, useEffect, useState } from "react";
import { TFunction, Trans, WithTranslation } from "react-i18next";
import { withTranslationReady } from "common/i18n/withTranslationReady";
import {
  RideButton,
  RideButtonSize,
  RideButtonVariant
} from "uiLibrary/designSystem/RideButton/RideButton";
import { QuestionIcon } from "sharedComponents/icons/DesignSystemIcons/QuestionIcon";
import { useRideFormik } from "lib/hooks/useRideFormik";
import { Form, FormikProvider } from "formik";
import { UnifiedShortOrderFormValues } from "lib/models/client/UnifiedShortOrderForm/UnifiedShortOrderFormValues";
import RideModal, { RideModalBody } from "uiLibrary/designSystem/RideModal/RideModal";
import { Typography } from "uiLibrary/designSystem/Typography/Typography";
import {
  RadioSelectVariant,
  RideRadioSelect
} from "uiLibrary/components/RideRadioSelect/RideRadioSelect";
import { FeatureFlags, LegalForm } from "global-query-types";
import { RideDropdownSelect } from "uiLibrary/components/RideDropdownSelect/RideDropdownSelect";
import {
  StepContent,
  StepContinueButton,
  StepHeader,
  StepLayout
} from "client/components/ShortOrderFormEngine/ShortOrderFormLayout/StepLayout/StepLayout";
import { useRideField } from "lib/hooks/useRideField";
import { FeatureFlagService } from "lib/services/FeatureFlagService/FeatureFlagService";
import {
  ExternalCompanyDataByName_externalCompanyDataByName_companies,
  ExternalCompanyDataByName_externalCompanyDataByName_companies_companyData
} from "lib/api/queries/graphql/ExternalCompanyDataByName";
import SelectedExternalCompanyCard from "client/components/SelectedExternalCompanyCard/SelectedExternalCompanyCard";
import { ExternalCompanySearchInputContainer } from "client/components/ExternalCompanySearchInput/ExternalCompanySearchInputContainer";
import { BusinessIcon } from "sharedComponents/icons/DesignSystemIcons/BusinessIcon";
import { UnifiedShortOrderFormStepProps } from "client/components/UnifiedShortOrderForm/UnifiedShortOrderForm.partials";
import * as Yup from "yup";
import { removeTypenames, WithoutTypenames } from "../../../../testTools/helpers/typename";
import { CreateCompanyLeadForUnifiedShortOrderFormVariables } from "../../../../lib/api/mutations/graphql/CreateCompanyLeadForUnifiedShortOrderForm";
import { ErrorMessageWithT } from "components/generic/ErrorMessage";

export interface CompanyInformationStepProps extends UnifiedShortOrderFormStepProps {
  createCompanyLeadHandler: ({
    companyData,
    companyAddress,
    hasCorporateShareholders,
    contactEmail
  }: CreateCompanyLeadForUnifiedShortOrderFormVariables) => Promise<void>;
}

const supportedLegalForms = [LegalForm.GmbH, LegalForm.UG, LegalForm.AG];

const CompanyInformationStep = ({
  t,
  initialValues,
  persistData,
  goToNextStep,
  goToPreviousStep,
  createCompanyLeadHandler
}: CompanyInformationStepProps & WithTranslation) => {
  const useCompanySearchInput = FeatureFlagService.instance.isEnabled(
    FeatureFlags.CompanySearchInputShortOrderForm
  );
  const formik = useRideFormik({
    initialValues: {
      ...initialValues,
      customerAlreadyHasCompany: initialValues.customerAlreadyHasCompany ?? undefined,
      companyName: initialValues.companyName ?? undefined,
      companyLegalForm: initialValues.companyLegalForm ?? undefined,
      hasCorporateShareholders: initialValues.hasCorporateShareholders ?? undefined
    },
    validationSchema: CompanyInformationStepSchema(t, useCompanySearchInput),
    onSubmit: async () => {
      persistData(formik.values);

      if (useCompanySearchInput && formik.values.customerAlreadyHasCompany === "yes") {
        await createCompanyLeadHandler({
          companyData: {
            companyName: formik.values.companyName ?? "",
            legalForm: (formik.values.companyLegalForm as LegalForm) ?? undefined,
            registerCourt: formik.values.registerCourt ?? undefined,
            registration: formik.values.registration ?? undefined
          },
          companyAddress: formik.values.companyAddress,
          hasCorporateShareholders: formik.values.hasCorporateShareholders === "yes",
          contactEmail: formik.values.email ?? ""
        });
      }

      goToNextStep();
    }
  });

  return (
    <FormikProvider value={formik}>
      <Form>
        <CompanyInformationLayout
          t={t}
          goToPreviousStep={goToPreviousStep}
          persistData={persistData}
          formik={formik}>
          <CompanyInformationContent t={t} />
        </CompanyInformationLayout>
      </Form>
    </FormikProvider>
  );
};

const CompanyInformationContent = ({ t }: { t: TFunction }) => {
  const customerAlreadyHasCompanyField = useRideField("customerAlreadyHasCompany");
  const companyFoundingServiceField = useRideField("companyFoundingService");
  const legalFormField = useRideField("companyLegalForm");
  const registerCourtField = useRideField("registerCourt");
  const registrationField = useRideField("registration");
  const lowTaxBrokerField = useRideField("lowTaxBroker");
  const hasCorporateShareholdersField = useRideField("hasCorporateShareholders");
  const northDataUniqueKeyField = useRideField("northDataUniqueKey");
  const companyNameField = useRideField("companyName");
  const companyAddress = useRideField("companyAddress");

  const hasSelectedCompany = !!registrationField.value;
  const useCompanySearchInput = FeatureFlagService.instance.isEnabled(
    FeatureFlags.CompanySearchInputShortOrderForm
  );

  const [fillCompanyInformationManually, setFillCompanyInformationManually] = useState<boolean>(
    (!!legalFormField.value && !hasSelectedCompany) || !useCompanySearchInput
  );

  useEffect(() => {
    if (customerAlreadyHasCompanyField.value === "yes") {
      companyFoundingServiceField.setValue(false);
    } else {
      companyFoundingServiceField.setValue(undefined);
      registerCourtField.setValue(undefined);
      registrationField.setValue(undefined);
      legalFormField.setValue(undefined);
      companyNameField.setValue(undefined);
      companyAddress.setValue(undefined);
      hasCorporateShareholdersField.setValue(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAlreadyHasCompanyField.value]);

  useEffect(() => {
    if (legalFormField.value === LegalForm.AG) {
      lowTaxBrokerField.setValue(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalFormField.value]);

  const onSelectCompanyHandler = (company) => {
    legalFormField.setValue(company?.companyData.legalForm);
    registerCourtField.setValue(company?.companyData.registerCourt);
    registrationField.setValue(company?.companyData.registration);
    companyAddress.setValue(removeTypenames(company?.companyAddress));
    hasCorporateShareholdersField.setValue(
      company === undefined ? undefined : company.hasCorporateShareholders ? "yes" : "no"
    );
    northDataUniqueKeyField.setValue(company?.northDataUniqueKey);
    setFillCompanyInformationManually(false);
  };

  const selectedCompany = hasSelectedCompany
    ? {
        companyName: companyNameField.value,
        registration: registrationField.value,
        registerCourt: registerCourtField.value,
        legalForm: legalFormField.value,
        leiNumber: null
      }
    : undefined;

  return (
    <>
      <DoYouHaveACompanyQuestion t={t} />
      {customerAlreadyHasCompanyField.value === "yes" && useCompanySearchInput && (
        <ShortOrderFormCompanySearchInput
          t={t}
          onSelect={onSelectCompanyHandler}
          initialSelectedItem={selectedCompany}
          companyNotListedHandler={(show) => setFillCompanyInformationManually(show)}
        />
      )}
      {customerAlreadyHasCompanyField.value === "yes" && fillCompanyInformationManually && (
        <>
          <SelectYourCompanyLegalFormQuestion t={t} />
          <CorporateShareholdersQuestion t={t} />
        </>
      )}
    </>
  );
};

interface QuestionModalProps extends WithTranslation {
  onClose: () => void;
}

const WhyDoINeedACompanyModal = withTranslationReady(["generic"])(
  ({ t, onClose }: QuestionModalProps) => {
    return (
      <RideModal data-testid="why-do-i-need-a-gmbh-modal" title="" onClose={onClose}>
        <RideModalBody className="company-information-step__modal-body">
          <Typography category="Headline" size={100} weight={"Heavy"}>
            {t(
              "generic:short-order-form.company-information.do-you-have-a-company.why-do-i-need-a-company-modal.title"
            )}
          </Typography>

          <Typography category="Paragraph" size={100} weight={"Light"}>
            <Trans
              i18nKey={
                "generic:short-order-form.company-information.do-you-have-a-company.why-do-i-need-a-company-modal.content"
              }
            />
          </Typography>
        </RideModalBody>
      </RideModal>
    );
  }
);

const WhyAreWeAskingAboutShareholdersModal = withTranslationReady(["generic"])(
  ({ t, onClose }: QuestionModalProps) => {
    return (
      <RideModal
        data-testid="why-are-we-asking-about-shareholders-modal"
        title=""
        onClose={onClose}>
        <RideModalBody className="company-information-step__modal-body">
          <Typography category="Headline" size={100} weight={"Heavy"}>
            {t(
              "generic:short-order-form.company-information.corporate-shareholders.why-are-we-asking-about-shareholders-modal.title"
            )}
          </Typography>

          <Typography category="Paragraph" size={100} weight={"Light"}>
            <Trans
              i18nKey={
                "generic:short-order-form.company-information.corporate-shareholders.why-are-we-asking-about-shareholders-modal.content"
              }
            />
          </Typography>
        </RideModalBody>
      </RideModal>
    );
  }
);

const DoYouHaveACompanyQuestion = ({ t }: { t: TFunction }) => {
  const [showWhyDoINeedAGmbhModal, setShowWhyDoINeedAGmbhModal] = useState(false);

  return (
    <div data-testid="do-you-have-a-gmbh" className="company-information-step__question-section">
      {showWhyDoINeedAGmbhModal && (
        <WhyDoINeedACompanyModal onClose={() => setShowWhyDoINeedAGmbhModal(false)} />
      )}
      <Typography
        category="Headline"
        size={300}
        weight={"Heavy"}
        data-testid="do-you-have-a-gmbh-title">
        {t("generic:short-order-form.company-information.do-you-have-a-company.title")}
      </Typography>
      <RideButton
        data-testid="why-do-i-need-a-gmbh-button"
        type="button"
        variant={RideButtonVariant.GHOST}
        size={RideButtonSize.BIG}
        leftIcon={<QuestionIcon size={20} />}
        onClick={() => setShowWhyDoINeedAGmbhModal(true)}
        label={t(
          "generic:short-order-form.company-information.do-you-have-a-company.help-button-label"
        )}
      />
      <RideRadioSelect
        className="company-information-step__options"
        name={"customerAlreadyHasCompany"}
        options={[
          {
            label: t("generic:short-order-form.company-information.do-you-have-a-company.yes"),
            value: "yes"
          },
          {
            label: t("generic:short-order-form.company-information.do-you-have-a-company.no"),
            value: "no"
          }
        ]}
        variant={RadioSelectVariant.Light}
      />
    </div>
  );
};

const SelectYourCompanyLegalFormQuestion = ({ t }: { t: TFunction }) => {
  const useCompanySearchInput = FeatureFlagService.instance.isEnabled(
    FeatureFlags.CompanySearchInputShortOrderForm
  );

  return (
    <div
      data-testid="select-legal-form"
      className={`company-information-step__select-legal-form ${
        useCompanySearchInput ? "company-information-step__select-legal-form--v2" : ""
      }`}>
      <Typography
        category="Paragraph"
        size={100}
        weight={"Heavy"}
        data-testid="please-select-your-company-legal-form-title">
        {t(
          "generic:short-order-form.company-information.please-select-your-company-legal-form.title"
        )}
      </Typography>
      <RideDropdownSelect
        name="companyLegalForm"
        isClearable={true}
        options={supportedLegalForms.map((legalForm) => ({
          label: legalForm,
          value: legalForm
        }))}
      />
    </div>
  );
};

const CorporateShareholdersQuestion = ({ t }: { t: TFunction }) => {
  const [whyAreWeAskingAboutShareholdersModal, setwhyAreWeAskingAboutShareholdersModal] =
    useState(false);

  const useCompanySearchInput = FeatureFlagService.instance.isEnabled(
    FeatureFlags.CompanySearchInputShortOrderForm
  );

  return (
    <div
      data-testid="corporate-shareholders-question"
      className={
        useCompanySearchInput
          ? "company-information-step__corporate-shareholder-question"
          : "company-information-step__question-section company-information-step__corporate-shareholder-question-old-version"
      }>
      {whyAreWeAskingAboutShareholdersModal && (
        <WhyAreWeAskingAboutShareholdersModal
          onClose={() => setwhyAreWeAskingAboutShareholdersModal(false)}
        />
      )}
      <Typography
        category={useCompanySearchInput ? "Paragraph" : "Headline"}
        size={useCompanySearchInput ? 100 : 300}
        weight={"Heavy"}
        data-testid="corporate-shareholders-title">
        {t("generic:short-order-form.company-information.corporate-shareholders.title")}
      </Typography>
      <Typography
        category="Paragraph"
        size={100}
        weight={"Light"}
        data-testid="corporate-shareholders-info"
        className={`company-information-step__corporate-shareholders-info ${
          useCompanySearchInput ? "company-information-step__corporate-shareholders-info--v2" : ""
        }`}>
        {t("generic:short-order-form.company-information.corporate-shareholders.info")}
      </Typography>
      <RideButton
        data-testid="why-are-we-asking-about-shareholders-button"
        type="button"
        variant={RideButtonVariant.GHOST}
        size={RideButtonSize.BIG}
        leftIcon={<QuestionIcon size={20} />}
        onClick={() => setwhyAreWeAskingAboutShareholdersModal(true)}
        label={t(
          "generic:short-order-form.company-information.corporate-shareholders.why-are-we-asking-about-shareholders-modal.title"
        )}
      />
      <RideRadioSelect
        className={`company-information-step__options ${
          useCompanySearchInput ? "company-information-step__corporate-shareholders-options" : ""
        }`}
        name={"hasCorporateShareholders"}
        options={[
          {
            value: "yes",
            label: t("generic:short-order-form.company-information.corporate-shareholders.yes")
          },
          {
            value: "no",
            label: t("generic:short-order-form.company-information.corporate-shareholders.no")
          }
        ]}
        variant={RadioSelectVariant.Light}
      />
    </div>
  );
};

const CompanyInformationLayout = ({
  t,
  children,
  persistData,
  goToPreviousStep,
  formik
}: {
  t: TFunction;
  persistData: (values: UnifiedShortOrderFormValues) => void;
  goToPreviousStep: () => void;
  formik: any;
} & PropsWithChildren<{}>) => {
  return (
    <StepLayout data-testid="company-information-step">
      <StepContent>
        <StepHeader
          data-testid={"company-information-step-header"}
          title={t("generic:short-order-form.company-information.title")}
          subtitle={t("generic:short-order-form.company-information.subtitle")}
          backButtonHandler={() => {
            persistData(formik.values);
            goToPreviousStep();
          }}
        />
        <div>{children}</div>
      </StepContent>
      <StepContinueButton label={t("generic:continue")} />
    </StepLayout>
  );
};

const ShortOrderFormCompanySearchInput = ({
  t,
  onSelect,
  companyNotListedHandler,
  initialSelectedItem
}: {
  onSelect: (
    data: ExternalCompanyDataByName_externalCompanyDataByName_companies | undefined
  ) => void;
  t: TFunction;
  initialSelectedItem?: WithoutTypenames<ExternalCompanyDataByName_externalCompanyDataByName_companies_companyData>;
  companyNotListedHandler: (show: boolean) => void;
}) => {
  const [selectedItem, setSelectedItem] = useState<
    | WithoutTypenames<ExternalCompanyDataByName_externalCompanyDataByName_companies_companyData>
    | undefined
  >(initialSelectedItem);

  return (
    <div>
      <div className={"company-information-step__your-company"}>
        <BusinessIcon data-testid={"order-summary-header-icon"} />
        <Typography
          data-testid={"order-summary-header-title"}
          category="Paragraph"
          size={100}
          weight={"Heavy"}>
          {t("generic:short-order-form.company-information.your-company")}
        </Typography>
      </div>
      {selectedItem ? (
        <>
          <SelectedExternalCompanyCard
            externalCompanyData={selectedItem}
            onClose={() => {
              onSelect(undefined);
              setSelectedItem(undefined);
            }}
          />
          <ErrorMessageWithT name="companyLegalForm" extra={{}} />
        </>
      ) : (
        <div className={"company-information-step__company-search"}>
          <Typography
            category="Paragraph"
            size={100}
            weight={"Heavy"}
            data-testid="ride-company-search-label">
            {t("generic:short-order-form.company-information.company-search.title")}
          </Typography>
          <ExternalCompanySearchInputContainer
            onSelectCompany={(company) => {
              onSelect?.(company);
              setSelectedItem(company.companyData);
            }}
            inputName={"companyName"}
          />
          <Typography
            category="Paragraph"
            size={300}
            weight={"Light"}
            data-testid="ride-company-search-info"
            className={"company-information-step__company-search-info"}>
            {t("generic:short-order-form.company-information.company-search.info")}
          </Typography>
          <RideButton
            className={"company-information-step__company-not-listed"}
            data-testid={"company-not-in-the-list"}
            type="button"
            label={t("generic:short-order-form.company-information.company-not-in-the-list")}
            variant={RideButtonVariant.GHOST}
            size={RideButtonSize.SMALL}
            onClick={() => companyNotListedHandler(true)}
          />
        </div>
      )}
    </div>
  );
};

const CompanyInformationStepSchema = (t: TFunction, useCompanySearchInput: boolean) => {
  return Yup.object().shape({
    customerAlreadyHasCompany: Yup.string()
      .oneOf(["yes", "no"])
      .required(t("generic:validation-required")),
    companyName: useCompanySearchInput
      ? Yup.string().when("customerAlreadyHasCompany", {
          is: "yes",
          then: (schema) => schema.required(t("generic:validation-required"))
        })
      : Yup.string().notRequired(),
    companyLegalForm: Yup.string()
      .oneOf(["GmbH", "UG", "AG"], t("generic:unsupported-company-legal-form"))
      .when("customerAlreadyHasCompany", {
        is: "yes",
        then: (schema) => schema.required(t("generic:validation-required"))
      })
      .nullable(),
    hasCorporateShareholders: Yup.string()
      .oneOf(["yes", "no"])
      .when("customerAlreadyHasCompany", {
        is: "yes",
        then: (schema) => schema.required(t("generic:validation-required"))
      })
  });
};

export default withTranslationReady(["generic"])(CompanyInformationStep);
